<template>
  <div class="cso-login-with-link">
    <cso-component-state :componentState="state">
      <template v-slot:login>
        <div v-if="termsAndConditionsOutdated">
          <cso-login-terms-and-conditions
            :error-text="tacError"
            @termsAndConditionsAccepted="termsAndConditionsAccepted"
            @termsAndConditionsDenied="termsAndConditionsDenied" />
        </div>
        <form @submit.prevent="sendMagicLink" class="hidden-print">
          <fieldset>
            <legend>{{ isDoubleLogin ? $t('login.title.withLink') : $t('login.title.justLink') }}</legend>
            <h3>
              {{ isDoubleLogin ? $t('login.title.withLink') : $t('login.title.justLink') }}
            </h3>

            <div class="input--wrapper">
              <h4 class="normal-text">
                {{ $t('login.texts.withLinkMoreInfo') }}
                <cso-info-icon :content="$t('login.texts.infoIconText')" />
              </h4>
              <cso-input name="username"
                           class="custom-margin"
                           cypressName="no-pass-username-input"
                           :label="$t('login.labels.username')"
                           validationName="login.validations.username"
                           :validation="v$.username"
                           :isErrorMessage="!!error"
                           v-model="username">
              </cso-input>
              <div v-if="error"
                 class="error-text">
                  <p>{{ error }}</p>
              </div>
            </div>
            <cso-button
                        type="submit"
                        cypressName="request-email-btn"
                        :label="$t('login.buttons.send')"
                        class="custom-width">
            </cso-button>
          </fieldset>
        </form>
      </template>
      <template v-slot:finished>
        <div class="cso-login-sent-link">
          <div class="text-wrapper">
            <h3>
              <i class="icon" aria-label="Erfolgreich"></i>
              {{ $t('login.title.sentLink') }}
            </h3>
            <div class="text-container">
              <p>{{ $t('login.texts.sentLinkHeader') }}</p>
              <p>{{ $t('login.texts.sentLink') }}</p>
              <p class="username">{{ usernameHashed }}</p>
            </div>
          </div>
          <div class="buttons">
            <cso-button modifier="light-white"
                        type="submit"
                        @click="back"
                        class="back-link custom-width"
                        :label="$t('login.buttons.back')">
            </cso-button>
          </div>
        </div>
      </template>
    </cso-component-state>
  </div>
</template>

<script lang="ts">
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'

import * as DI from '@/api/digital-interface/client'
import di from '@/api/digital-interface'
import LoggingService from '@/common/LoggingService'
import Utils from '@/common/Utils'
import ComponentsStates from '@/common/ComponentsStates'
import Authentication from '@/common/Authentication'

import CsoComponentState from '@/components/core/composition/CsoComponentState.vue'
import CsoInfoIcon from '@/components/core/composition/CsoInfoIcon.vue'
import CsoInput from '@/components/core/form/CsoInput.vue'
import CsoLoginTermsAndConditions from './CsoLoginTermsAndConditions.vue'

export default {
  name: 'CsoLoginWithLink',

  components: {
    CsoComponentState,
    CsoInput,
    CsoInfoIcon,
    CsoLoginTermsAndConditions
  },

  props: {
    isDoubleLogin: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      state: ComponentsStates.login,
      username: '',
      error: '',
      currentLocation: location.href,
      termsAndConditionsOutdated: false,
      termsAndConditionsConfirmed: false,
      tacError: ''
    }
  },

  computed: {
    code (): string {
      return this.$route.query.id as string
    },

    usernameHashed (): string {
      return Utils.hashEmail(this.username) || ''
    },

    demo (): boolean {
      return 'demo' in this.$route.query
    }
  },

  setup () {
    return {
      v$: useVuelidate()
    }
  },

  async created () {
    if (this.code) {
      await this.loginUser()
    } else if (this.demo) {
      await this.loginDemo()
    }
  },

  methods: {
    async loginUser () {
      Utils.windowLoading(true)
      this.error = ''
      try {
        this.termsAndConditionsOutdated = false
        await Authentication.login({
          code: this.code,
          termsAndConditionsConfirmed: this.termsAndConditionsConfirmed
        })
      } catch (error) {
        this.handleError(error as Error)
      }
      Utils.windowLoading(false)
    },

    async loginDemo () {
      Utils.windowLoading(true)
      this.error = ''
      try {
        await Authentication.login({ demo: true })
      } catch (error) {
        this.handleError(error as Error)
      }
      Utils.windowLoading(false)
    },

    async sendMagicLink () {
      this.username = Utils.killSpace(this.username)
      if (!this.validate()) {
        return
      }
      this.pushToDataLayer()
      LoggingService.log(LoggingService.eventTypes.userEvent, 'Sent request for magic link')
      Utils.windowLoading(true)
      this.error = ''
      const currentToken = di.token
      try {
        const prozesse = await di.v13.getProzesse([], ['MAGICLINK'])
        if (prozesse.isEnabled('MAGICLINK')) {
          await di.v13.authenticateAnonymous()
          await di.v13.sendMagicLink(this.username)
        } else {
          this.error = prozesse.getError('MAGICLINK')
        }
      } catch (error) {
        this.handleError(error as Error)
      }
      di.token = currentToken
      Utils.windowLoading(false)
      if (this.error) {
        return
      }
      this.state = ComponentsStates.finished
      this.$emit('send')
    },

    async deleteAccount () {
      Utils.windowLoading(true)
      try {
        await di.v13.deleteAccount(this.code)
        this.$router.push({ query: { accountDeleted: 'true' } })
      } catch (error) {
        const message = (error as any)?.message || this.$t('general.errors.post')
        this.$emit('server-error', message)
      }
      Utils.windowLoading(false)
    },

    validate () {
      this.v$.$touch()
      return !this.v$.$invalid
    },

    back () {
      this.clear()
      this.state = ComponentsStates.login
      this.$emit('back')
    },

    clear () {
      this.username = ''
      this.v$.$reset()
    },

    pushToDataLayer () {
      const data = {
        event: 'magic_link'
      }
      Utils.pushToDataLayer(data)
    },

    handleError (error: Error) {
      LoggingService.error(LoggingService.eventTypes.authentication, 'Magic login error', error)
      if (error instanceof DI.ErrorResponse) {
        if (error.code === 'ERROR_NUTZUNGSBEDINGUNGEN_BESTAETIGUNG_ERFORDERLICH') {
          this.termsAndConditionsOutdated = true
          this.tacError = error.text
        } else {
          this.error = error.text
        }
      } else {
        throw error
      }
    },

    termsAndConditionsAccepted () {
      this.termsAndConditionsConfirmed = true
      this.loginUser()
    },

    termsAndConditionsDenied () {
      this.termsAndConditionsOutdated = false
      this.deleteAccount()
    }
  },

  validations () {
    return {
      username: {
        required
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.cso-login-with-link {

  .custom-margin {
    margin: 28px 0 12px 0;

    @include md-1 {
      margin-top: 16px;
    }

  }

  fieldset {
    padding: 0 30px;

    @include md {
      padding: 24px 6px;
    }
  }

  button.custom-width {
    @include sm {
      width: 100%;
    }
  }

  h3 {
    text-align: left;
    font-size: 24px;
    font-family: $font-family-bold;
    font-weight: normal;
    margin: 0 0 16px 0;

    @include sm {
      font-size: 22px;
    }

    .show-mobile {
      display: none;

      @include md {
        display: inline;
      }

    }
  }

  h4 {
    font-size: 16px;
    font-weight: normal;

    .cso-info-icon:before {
      display: inline-block;
      line-height: 1;
      margin-top: -8px;
    }

    @include xs {
      font-size: 14px;
    }
  }

  .error-text {
    line-height: 60px;
    min-height: 60px;

    @include error-message();

    p {
      display: inline-block;
      vertical-align: middle;
    }

  }

  .input--wrapper {
    height: 210px;

    @include md {
      height: auto;
    }
  }

  .cso-login-sent-link {
    padding: 0 24px;

    @include md-1 {
      padding: 24px 6px 0;
    }

    .text-wrapper {
      min-height: 248px;

      @include md-1 {
        min-height: 0;
      }
    }

    h3 {
      position: relative;
      margin: 0 0 16px;
      padding-left: 56px;

      i.icon {
        position: absolute;
        left: 0;
        top: 50%;
        transform: translate(0,-50%);
        height: 42px;
        width: 42px;
        background-repeat: no-repeat;
        background-size: cover;
        background-position: center;
        background-color: transparent;
        background-image: url(/static/icons/success.svg);
      }
    }

    .text-container {
      padding-bottom: 16px;

      @include sm {
        padding: 0;
      }

      p {
        font-size: 16px;
        line-height: 24px;
        padding-bottom: 16px;
        margin: 0;

        @include sm {
          padding-bottom: 12px;
        }
      }

      .username {
        color: $blue;
        font-weight: bold;
      }
    }

    .back-link {
      @include hidden-print;
      margin-bottom: 12px;
    }
  }
}
</style>
